import { useState } from 'react';
import { useGetLmsLookupTranslationsQuery, useAddLmsLookupTranslationMutation, useUpdateLmsLookupTranslationMutation, useDeleteLmsLookupTranslationMutation } from '../../../../api';
import toastService from '../../../../utils/toastService';
import { useLanguage } from '../../../../contexts/LanguageContext';

/**
 * LMS tanım listeleri (Eğitim Tipi, Eğitim Lokasyonu vb.) için generic CRUD veri/state hook'u.
 * `groupCode` (örn. 'TRAINING_TYPES') dışında Eğitim Tipi ve Eğitim Lokasyonu ekranları
 * aynı davranışı paylaşır — bkz. docs/LMS-Lookup-Translation-API.md.
 */
const useLmsLookupDefinitionsData = (groupCode) => {
    const { th } = useLanguage();
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1 });
    const [activeFilters, setActiveFilters] = useState({ code: '', text: '', isActive: null });

    const {
        data: lookupApi,
        isLoading,
        isFetching,
        error,
        refetch
    } = useGetLmsLookupTranslationsQuery(
        {
            groupCode,
            code: activeFilters.code || undefined,
            text: activeFilters.text || undefined,
            // isActive: 0 geçerli bir seçim (Pasif filtresi) olduğu için `|| undefined` kullanılmıyor —
            // aksi halde 0 seçimi sessizce "filtre yok" (varsayılan: yalnızca aktif) haline döner.
            // Bkz. training-catalog/hooks/useTrainingCatalogData.js aynı desen.
            isActive: activeFilters.isActive === 0 || activeFilters.isActive === 1 ? activeFilters.isActive : undefined,
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows
        },
        { refetchOnMountOrArgChange: true }
    );

    const [addMutation] = useAddLmsLookupTranslationMutation();
    const [updateMutation] = useUpdateLmsLookupTranslationMutation();
    const [deleteMutation] = useDeleteLmsLookupTranslationMutation();

    const saveDefinition = async (formData) => {
        try {
            if (formData.id) {
                await updateMutation(formData).unwrap();
                toastService.success(th('lmsLookupDefinitions.saveSuccess'), th('common.success'));
            } else {
                await addMutation({ ...formData, groupCode }).unwrap();
                toastService.success(th('lmsLookupDefinitions.saveSuccess'), th('common.success'));
            }
            refetch();
            return true;
        } catch {
            toastService.error(th('lmsLookupDefinitions.saveError'), th('common.error'));
            return false;
        }
    };

    const deleteDefinition = async (id) => {
        await deleteMutation(id).unwrap();
        refetch?.();
    };

    const onPage = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            page: event.page + 1,
            first: event.first,
            rows: event.rows
        }));
    };

    const handleFilterChange = (updated) => {
        setActiveFilters({
            code: updated.code || '',
            text: updated.text || '',
            isActive: updated.isActive === 0 || updated.isActive === 1 ? updated.isActive : null
        });
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const initFilters = () => {
        setActiveFilters({ code: '', text: '', isActive: null });
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    return {
        lookupApi: lookupApi || { items: [], totalCount: 0 },
        isLoading,
        isFetching,
        error,
        lazyParams,
        onPage,
        activeFilters,
        handleFilterChange,
        initFilters,
        saveDefinition,
        deleteDefinition,
        refetch
    };
};

export default useLmsLookupDefinitionsData;
